import CloseIcon from '@mui/icons-material/Close';
import DialpadIcon from '@mui/icons-material/Dialpad';
import {
    ButtonBase,
    Divider,
    Grid,
    IconButton,
    Popover,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { sendDigits } from '../../../store/dialer';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';

export default function ActiveCallDialPad() {
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const dispatch = useAppDispatch();

    const { classes } = useStyles();

    const digitsSent = useAppSelector(state => state.dialer.sendDigits);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const sendDigit = React.useCallback(
        (number: string) => {
            dispatch(sendDigits(number));
        },
        [dispatch],
    );

    return (
        <>
            <Tooltip title="Dial Pad">
                <IconButton size="large" onClick={handleClick}>
                    <DialpadIcon />
                </IconButton>
            </Tooltip>
            <Popover
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
            >
                <Grid container flexDirection="row" spacing={1} className={classes.container} p={1}>
                    <Grid container item justifyContent="flex-end">
                        <IconButton onClick={handlePopoverClose}>
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <Grid container item justifyContent="center">
                        <Grid item>
                            <Typography className={classes.digits}>
                                {digitsSent?.join('')}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Divider flexItem />
                    <Grid item container spacing={1}>
                        {dialPadData.map(digit => (
                            <Grid container key={`${digit.number}`} item xs={4}>
                                <PhoneButton onClick={() => sendDigit(digit.number)}>
                                    <Typography variant="h5">{digit.number}</Typography>
                                    <Typography variant="caption">
                                        {digit.characters || ' '}
                                    </Typography>
                                </PhoneButton>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Popover>
        </>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    iconBtn: {
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(2),
    },
    digits: {
        textAlign: 'center',
        fontSize: 36,
        width: '100%',
        wordBreak: 'break-all',
        minHeight: 54,
    },
    container: {
        maxWidth: 300,
    },
}));

const PhoneButton = styled(ButtonBase)(({ theme }) => ({
    position: 'relative',
    height: 75,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.backgroundMainLayout.main,
    '&:hover': {
        backgroundColor: theme.palette.backgroundMainLayout.light,
    },
}));

type DialPadEntry = {
    number: string;
    characters: string;
};

const dialPadData: DialPadEntry[] = [
    { number: '1', characters: '' },
    { number: '2', characters: 'ABC' },
    { number: '3', characters: 'DEF' },
    { number: '4', characters: 'GHI' },
    { number: '5', characters: 'JKL' },
    { number: '6', characters: 'MNO' },
    { number: '7', characters: 'PQRS' },
    { number: '8', characters: 'TUV' },
    { number: '9', characters: 'WXYZ' },
    { number: '*', characters: '' },
    { number: '0', characters: '' },
    { number: '#', characters: '' },
];
